<template>
  <div>
    <header
      class="flex justify-between items-center px-2 py-2.5 z-10 bg-white"
    >
      <div class="flex items-center">
        <img
          class="avatar mr-2 w-7 h-7 rounded-full"
          src="/static/roundtable_options_create/genie_icon.svg"
        />
        <div class="flex flex-col font-manrope">
          <span class="font-semibold leading-4"> Follow-Up Questions </span>
          <span class="text-xs text-gray-500 leading-3">
            {{ recapDateTimeAgo }}
          </span>
        </div>
      </div>
      <div class="flex items-center space-x-1">
        <div v-if="recapQuestions.length">
          <Tooltip
            text="Edit Follow-Up Questions"
            styleTooltip="top-0 right-full mr-2"
            backgroundColor="bg-gray-700"
          >
            <button
              @click="editFollowUpQuestions"
              :disabled="loading"
              class="sample hover:bg-gray-100 text-gray-600 rounded-full w-8 h-8"
            >
              <i class="fa-regular fa-pen-to-square"></i>
            </button>
          </Tooltip>
        </div>
        <Tooltip
          text="Regenerate Follow-Up Questions"
          styleTooltip="top-0 right-full mr-2"
          backgroundColor="bg-gray-700"
        >
          <button
            @click="regenerateFollowUpQuestions"
            :disabled="loading"
            class="sample flex justify-center hover:bg-pink-100 text-pinky items-center rounded-full w-8 h-8"
          >
            <i :class="loading ? 'animate-spin' : ''" class="fa-solid fa-rotate"></i>
          </button>
        </Tooltip>
        <button
          class="flex justify-center items-center hover:bg-gray-100 w-8 text-gray-600 h-8 rounded-full"
          @click="$emit('backToOnlineStudentList')"
        >
          <i class="fa-solid fa-close text-base" />
        </button>
      </div>
    </header>
    <div class="px-2.5 pt-2 pb-6 max-height-list">
      <div>
        <ul class="space-y-2">
          <li
            v-for="(q, index) in recapQuestions"
            :key="index"
            class="flex items-start gap-3 font-manrope rounded-lg cursor-pointer transition-colors duration-200"
          >
            <span>
              {{ index + 1 }}.
            </span>
            <p class="text-gray-700 leading-snug">
              {{ q.question }}
            </p>
          </li>
        </ul>
        <div class="mt-6">
          <div class="flex flex-col mb-6">
            <h6 class="font-semibold mb-1 text-gray-700 font-manrope">
              Share Follow-Up Questions
            </h6>
            <span class="text-xs font-manrope text-gray-600">
              Click below to share these follow-up questions with your students.
              You can edit the questions.
            </span>
          </div>
          <div class="flex flex-col space-y-2">
            <button
              @click="addQuestionCurrentRT"
              class="sample px-5 py-3.5 border border-gray-200 hover:bg-gray-50 text-left border-solid rounded-lg font-manrope cursor-pointer disabled:opacity-60"
              :class="loading || genieRecap.is_current_rt ? '' : 'hover:border-gray-300'"
              :disabled="loading || genieRecap.is_current_rt"
            >
              <div class="flex items-center space-x-4">
                <img
                  src="/static/roundtable_options_create/current_roundtable_icon.svg"
                  class="w-8 h-8"
                />
                <div class="flex flex-col">
                  <h6 class="font-medium text-gray-700">Current RoundTable</h6>
                  <span class="text-xs text-gray-400">
                    Students can comment one the Genie Recap thread
                  </span>
                </div>
              </div>
            </button>
            <button
              @click="startFollowUpWRT"
              :disabled="loading"
              id="written-roundtable-follow-up"
              class="px-5 py-3.5 border hover:border-gray-300 text-left hover:bg-gray-50 border-gray-200 border-solid rounded-lg font-manrope cursor-pointer disabled:opacity-60"
            >
              <div class="flex items-center space-x-4">
                <div
                  class="flex items-center justify-center rounded-full h-8 w-8 bg-blue"
                >
                  <img
                    src="/static/roundtable_icons/written_icon.svg"
                    class="w-4 h-4"
                  />
                </div>
                <div class="flex flex-col">
                  <h6 class="font-medium text-gray-700">Written RoundTable</h6>
                  <span class="text-xs text-gray-400">
                    Create a new Written RoundTable for the class
                  </span>
                </div>
              </div>
            </button>
            <button
              @click="startFollowUpVRT"
              :disabled="loading"
              id="verbal-roundtable-follow-up"
              class="px-5 py-3.5 border hover:border-gray-300 hover:bg-gray-50 border-solid text-left border-gray-200 rounded-lg font-manrope cursor-pointer disabled:opacity-60"
            >
              <div class="flex items-center space-x-4">
                <div
                  class="flex items-center justify-center rounded-full h-8 w-8 bg-green-light"
                >
                  <img
                    src="/static/roundtable_icons/verbal_icon.svg"
                    class="w-4 h-4"
                  />
                </div>
                <div class="flex flex-col">
                  <h6 class="font-medium text-gray-700">Verbal RoundTable</h6>
                  <span class="text-xs text-gray-400">
                    Create a new Verbal RoundTable for the class
                  </span>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import moment from 'moment';
  import { mapGetters } from 'vuex';
  import { EventBus } from 'utils/event-bus';
  import Tooltip from 'components/Tooltip.vue';

  export default {
    data() {
      return {
        loading: false,
        recap: {},
      };
    },
    components: {
      Tooltip,
    },
    computed: {
      ...mapGetters([
        'getCurrentRT',
        'getProfile',
        'getSubmissionsBySubmitStatus',
      ]),
      genieRecap() {
        return this.getCurrentRT.genie_recap || this.recap;
      },
      recapQuestions() {
        return this.genieRecap &&
          this.genieRecap.questions &&
          this.genieRecap.questions.length
          ? this.genieRecap.questions
          : [];
      },
      recapDateTimeAgo() {
        return this.genieRecap.updated_at
          ? moment(this.genieRecap.updated_at, moment.ISO_8601).fromNow()
          : null;
      },
      canCreateActivities() {
        return Boolean(
          this.getProfile.is_paid || this.getProfile.activity_passes
        );
      },
      limitOfRoundtablesInSequence() {
        return this.getCurrentRT.roundtables_sequence.length >= 5;
      },
    },
    methods: {
      regenerateFollowUpQuestions() {
        this.loading = true;
        const payload = { summary: this.genieRecap.prompt_summary };
        if (
          this.getSubmissionsBySubmitStatus.submitted &&
          this.getSubmissionsBySubmitStatus.submitted.length >
            this.genieRecap.submissions_count
        ) {
          EventBus.$emit('regenerateSummaryAndQuestions');
          return;
        }
        this.$store
          .dispatch('genieRecapGenerateFollowUpQuestions', {
            id: this.genieRecap.id,
            roundtable_id: this.getCurrentRT.id,
            payload,
          })
          .then(() => {
            const toast = {
              type: 'Success',
              message: 'Questions successfully regenerated.',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
          })
          .catch((err) => {
            console.error(err);
            const toast = {
              type: 'Error',
              message:
                'Sorry! Something wrong happenned on regenerating questions! Try again!',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
          })
          .finally(() => (this.loading = false));
      },
      addQuestionCurrentRT() {
        if (!this.genieRecap.is_current_rt) {
          this.loading = true;
          const payload = {
            roundtable_id: this.getCurrentRT.id,
            is_current_rt: true,
          };
          this.$store
            .dispatch('updateGenieRecap', { id: this.genieRecap.id, payload })
            .then(() => {
              const toast = {
                type: 'Success',
                message: 'Questions successfully added in current RoundTable.',
                time: 5000,
              };
              EventBus.$emit('createToast', toast);
            })
            .catch((err) => {
              console.error(err);
              const toast = {
                type: 'Error',
                message:
                  'Sorry! Something wrong happenned on adding questions! Try again!',
                time: 5000,
              };
              EventBus.$emit('createToast', toast);
            })
            .finally(() => (this.loading = false));
        }
      },
      startFollowUpWRT() {
        if (!this.canCreateActivities) {
          const toast = {
            type: 'Info',
            message: 'You have (0) RoundTables left. Want more? Delete old ones or upgrade account.',
            time: 5000,
          };
          EventBus.$emit('createToast', toast);
          return;
        }
        if (this.limitOfRoundtablesInSequence) {
          const toast = {
            type: 'Info',
            message: 'You have reached the limit of RoundTables per Sequence (5).',
            time: 5000,
          };
          EventBus.$emit('createToast', toast);
          return;
        }
        this.$emit('startFollowUp', 'written');
      },
      startFollowUpVRT() {
        if (!this.canCreateActivities) {
          const toast = {
            type: 'Info',
            message:
              'You have (0) RoundTables left. Want more? Delete old ones or upgrade account.',
            time: 5000,
          };
          EventBus.$emit('createToast', toast);
          return;
        }
        if (this.limitOfRoundtablesInSequence) {
          const toast = {
            type: 'Info',
            message:
              'You have reached the limit of RoundTables per Sequence (5).',
            time: 5000,
          };
          EventBus.$emit('createToast', toast);
          return;
        }
        this.$emit('startFollowUp', 'verbal');
      },
      editFollowUpQuestions() {
        this.$emit('editFollowUpQuestions');
      },
    },
    mounted() {
      EventBus.$on('GenieRecapSideBarloading', () => (this.loading = false));
    },
    beforeDestroy() {
      EventBus.$off('GenieRecapSideBarloading');
    },
  };
</script>
<style scoped>
  .max-height-list {
    max-height: calc(100vh - 202px);
    overflow-y: auto;
  }
  .loader {
    border-top: 3px solid #eb2d7c;
    border-right: 3px solid #eb2d7c;
    border-bottom: 3px solid #eb2d7c;
    border-left: 3px solid #ffffff;
    -webkit-transform: translateZ(0);
    -ms-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-animation: load8 1.1s infinite linear;
    animation: load8 1.1s infinite linear;
  }
</style>



// WEBPACK FOOTER //
// genieRecapSideBar.vue?127f570b